import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Search, Clock, AlertTriangle, CheckCircle2, Users, Eye, ChevronRight,
  Building2, FileText, CalendarDays, X, ChevronDown, ChevronLeft,
} from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { paiActiveData } from '@/data/mockData';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

const priorityConfig = {
  alta: { variant: 'alta', label: 'Alta', dot: 'bg-destructive' },
  media: { variant: 'media', label: 'Media', dot: 'bg-warning' },
  bassa: { variant: 'bassa', label: 'Bassa', dot: 'bg-success' },
};

const statoLabels = {
  da_accettare: 'Da Accettare',
  presa_in_carico: 'Presa in Carico',
  in_valutazione: 'In Valutazione',
  pai_completato: 'PAI Completato',
  assegnazione: 'In Assegnazione',
};

const priorityOptions = [
  { key: 'alta', label: 'Alta (U-A)', dot: 'bg-destructive' },
  { key: 'media', label: 'Media (U-B)', dot: 'bg-warning' },
  { key: 'bassa', label: 'Bassa (U-C)', dot: 'bg-success' },
];

const statoFilterOptions = [
  { key: 'tutti', label: 'Tutti gli stati' },
  { key: 'da_accettare', label: 'Da Accettare' },
  { key: 'in_valutazione', label: 'In Valutazione' },
  { key: 'presa_in_carico', label: 'Presa in Carico' },
  { key: 'sospesa', label: 'Sospesa' },
];

const ITEMS_PER_PAGE = 15;

function parseItalianDate(str) {
  if (!str) return null;
  const parts = str.split('/');
  if (parts.length !== 3) return null;
  return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
}

function formatDateShort(date) {
  if (!date) return '';
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function isPAIExpiringSoon(patientId) {
  const pai = paiActiveData[patientId];
  if (!pai?.dataFinePAI) return false;
  const endDate = parseItalianDate(pai.dataFinePAI);
  if (!endDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
  return diffDays >= 0 && diffDays <= 15;
}

export default function SegreteriaDashboard() {
  const { patients, showPendingNotifications } = useApp();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('da_accettare');
  const [currentPage, setCurrentPage] = useState(1);

  // Advanced filters
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [statoFilter, setStatoFilter] = useState('tutti');
  const [priorityPopoverOpen, setPriorityPopoverOpen] = useState(false);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [statoPopoverOpen, setStatoPopoverOpen] = useState(false);
  const [showOnlyExpiring, setShowOnlyExpiring] = useState(false);

  useEffect(() => { showPendingNotifications('segreteria'); }, [showPendingNotifications]);

  const hasActiveFilters = selectedPriorities.length > 0 || dateRange.from || statoFilter !== 'tutti';

  const resetFilters = useCallback(() => {
    setSelectedPriorities([]);
    setDateRange({ from: undefined, to: undefined });
    setStatoFilter('tutti');
    setCurrentPage(1);
  }, []);

  const togglePriority = useCallback((key) => {
    setSelectedPriorities(prev =>
      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
    );
    setCurrentPage(1);
  }, []);

  const filteredPatients = useMemo(() => {
    return patients.filter(p => {
      const matchesSearch = searchTerm === '' ||
        `${p.cognome} ${p.nome}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.codiceFiscale.toLowerCase().includes(searchTerm.toLowerCase());

      let matchesTab;
      if (activeTab === 'da_accettare') {
        matchesTab = p.stato === 'da_accettare';
      } else {
        matchesTab = p.stato !== 'da_accettare';
      }

      const matchesPriority = selectedPriorities.length === 0 || selectedPriorities.includes(p.priorita);

      let matchesDate = true;
      if (dateRange.from || dateRange.to) {
        const pDate = parseItalianDate(p.dataRichiesta);
        if (pDate) {
          if (dateRange.from) {
            const from = new Date(dateRange.from);
            from.setHours(0, 0, 0, 0);
            if (pDate < from) matchesDate = false;
          }
          if (dateRange.to) {
            const to = new Date(dateRange.to);
            to.setHours(23, 59, 59, 999);
            if (pDate > to) matchesDate = false;
          }
        }
      }

      const matchesStato = statoFilter === 'tutti' || p.stato === statoFilter;

      const matchesExpiring = !showOnlyExpiring || activeTab !== 'prese_in_carico' || isPAIExpiringSoon(p.id);

      return matchesSearch && matchesTab && matchesPriority && matchesDate && matchesStato && matchesExpiring;
    });
  }, [patients, searchTerm, activeTab, selectedPriorities, dateRange, statoFilter, showOnlyExpiring]);

  // Pagination
  const totalItems = filteredPatients.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedPatients = filteredPatients.slice(startIdx, startIdx + ITEMS_PER_PAGE);
  const showStart = totalItems > 0 ? startIdx + 1 : 0;
  const showEnd = Math.min(startIdx + ITEMS_PER_PAGE, totalItems);

  const stats = useMemo(() => {
    const totale = patients.length;
    const daAccettare = patients.filter(p => p.stato === 'da_accettare').length;
    const preseInCarico = patients.filter(p => p.stato !== 'da_accettare').length;
    const urgenti = patients.filter(p => p.priorita === 'alta' && p.stato === 'da_accettare').length;
    return { totale, daAccettare, preseInCarico, urgenti };
  }, [patients]);

  const dateRangeLabel = useMemo(() => {
    if (!dateRange.from && !dateRange.to) return null;
    if (dateRange.from && dateRange.to) return `${formatDateShort(dateRange.from)} — ${formatDateShort(dateRange.to)}`;
    if (dateRange.from) return `Dal ${formatDateShort(dateRange.from)}`;
    return `Al ${formatDateShort(dateRange.to)}`;
  }, [dateRange]);

  return (
    <AppLayout role="segreteria">
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Triage & Richieste</h1>
          <p className="text-sm text-muted-foreground mt-1">Gestione richieste Assistenza Domiciliare Integrata</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Totale Richieste', value: stats.totale, icon: FileText, color: 'text-foreground' },
            { label: 'Da Accettare', value: stats.daAccettare, icon: Clock, color: 'text-warning' },
            { label: 'Prese in Carico', value: stats.preseInCarico, icon: CheckCircle2, color: 'text-success' },
            { label: 'Urgenti', value: stats.urgenti, icon: AlertTriangle, color: 'text-destructive' },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} className="shadow-card">
                <CardContent className="p-4 lg:p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{stat.label}</span>
                    <Icon className={`w-4 h-4 ${stat.color}`} />
                  </div>
                  <p className="text-2xl font-semibold text-foreground">{stat.value}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main Table Card */}
        <Card className="shadow-card">
          {/* Row 1: Title + Search */}
          <CardHeader className="pb-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="text-base">Richieste ADI</CardTitle>
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Cerca per nome o codice fiscale..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  data-testid="search-input"
                />
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-4">
            {/* Row 2: Tabs (LEFT) + Filters (RIGHT) — same line */}
            <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
              {/* LEFT: Tabs */}
              <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setCurrentPage(1); }}>
                <TabsList className="h-9">
                  <TabsTrigger value="da_accettare" className="gap-1.5 px-3 text-xs">
                    <Clock className="w-3.5 h-3.5" />
                    Da Accettare
                    <Badge variant="alta" className="ml-1 text-[10px] px-1.5 py-0">{stats.daAccettare}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="prese_in_carico" className="gap-1.5 px-3 text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Prese in Carico
                    <Badge variant="success" className="ml-1 text-[10px] px-1.5 py-0">{stats.preseInCarico}</Badge>
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* RIGHT: Filters */}
              <div className="flex items-center gap-2 flex-wrap" data-testid="filter-toolbar">
                {/* A. Filtro Priorita */}
                <Popover open={priorityPopoverOpen} onOpenChange={setPriorityPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={`h-8 gap-1.5 text-xs font-normal border-border/80 ${selectedPriorities.length > 0 ? 'border-primary/40 bg-primary-muted text-foreground font-medium' : 'text-muted-foreground'}`}
                      data-testid="filter-priority-trigger"
                    >
                      {selectedPriorities.length > 0 && (
                        <span className="flex gap-0.5 mr-0.5">
                          {selectedPriorities.map(k => (
                            <span key={k} className={`w-1.5 h-1.5 rounded-full ${priorityOptions.find(o => o.key === k)?.dot}`} />
                          ))}
                        </span>
                      )}
                      {selectedPriorities.length > 0 ? `Priorita (${selectedPriorities.length})` : 'Priorita'}
                      <ChevronDown className="w-3 h-3 opacity-40" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-52 p-2" align="end">
                    <div className="space-y-0.5">
                      {priorityOptions.map(opt => (
                        <label
                          key={opt.key}
                          className="flex items-center gap-2.5 px-2 py-1.5 rounded-md cursor-pointer hover:bg-muted/60 transition-colors"
                          data-testid={`filter-priority-${opt.key}`}
                        >
                          <Checkbox
                            checked={selectedPriorities.includes(opt.key)}
                            onCheckedChange={() => togglePriority(opt.key)}
                            className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                          />
                          <span className={`w-2 h-2 rounded-full ${opt.dot} flex-shrink-0`} />
                          <span className="text-sm">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    {selectedPriorities.length > 0 && (
                      <>
                        <div className="border-t border-border my-1.5" />
                        <button
                          onClick={() => { setSelectedPriorities([]); setCurrentPage(1); }}
                          className="w-full text-left px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted/60"
                        >
                          Cancella selezione
                        </button>
                      </>
                    )}
                  </PopoverContent>
                </Popover>

                {/* B. Date Range */}
                <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={`h-8 gap-1.5 text-xs font-normal border-border/80 ${dateRange.from ? 'border-primary/40 bg-primary-muted text-foreground font-medium' : 'text-muted-foreground'}`}
                      data-testid="filter-date-trigger"
                    >
                      <CalendarDays className="w-3.5 h-3.5" />
                      {dateRangeLabel || 'Periodo'}
                      <ChevronDown className="w-3 h-3 opacity-40" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => { setDateRange(range || { from: undefined, to: undefined }); setCurrentPage(1); }}
                      numberOfMonths={1}
                      className="rounded-lg p-3"
                    />
                    {dateRange.from && (
                      <div className="border-t border-border px-3 py-2">
                        <button
                          onClick={() => { setDateRange({ from: undefined, to: undefined }); setCurrentPage(1); }}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Cancella date
                        </button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>

                {/* C. Filtro Stato */}
                <Popover open={statoPopoverOpen} onOpenChange={setStatoPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={`h-8 gap-1.5 text-xs font-normal border-border/80 ${statoFilter !== 'tutti' ? 'border-primary/40 bg-primary-muted text-foreground font-medium' : 'text-muted-foreground'}`}
                      data-testid="filter-stato-trigger"
                    >
                      {statoFilter !== 'tutti' ? statoFilterOptions.find(o => o.key === statoFilter)?.label : 'Stato'}
                      <ChevronDown className="w-3 h-3 opacity-40" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-1" align="end">
                    {statoFilterOptions.map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => { setStatoFilter(opt.key); setStatoPopoverOpen(false); setCurrentPage(1); }}
                        className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors ${
                          statoFilter === opt.key ? 'bg-primary-muted text-foreground font-medium' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                        }`}
                        data-testid={`filter-stato-${opt.key}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>

                {/* Reset */}
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1 text-xs text-muted-foreground hover:text-destructive px-2"
                    onClick={resetFilters}
                    data-testid="filter-reset-btn"
                  >
                    <X className="w-3 h-3" />
                    Resetta
                  </Button>
                )}
              </div>
            </div>

            {/* Toggle PAI in Scadenza — only visible in Prese in Carico tab */}
            {activeTab === 'prese_in_carico' && (
              <div className="flex items-center gap-3 mb-4 p-3 bg-warning/8 border border-warning/20 rounded-xl" data-testid="pai-expiring-toggle-wrapper">
                <Switch
                  id="pai-expiring"
                  checked={showOnlyExpiring}
                  onCheckedChange={(v) => { setShowOnlyExpiring(v); setCurrentPage(1); }}
                  data-testid="pai-expiring-toggle"
                />
                <Label htmlFor="pai-expiring" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-warning" />
                  Mostra solo PAI in Scadenza
                  {showOnlyExpiring && (
                    <Badge variant="warning" className="text-[10px] ml-1">Filtro attivo</Badge>
                  )}
                </Label>
              </div>
            )}

            {/* Table Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full" data-testid="patients-table">
                <thead>
                  <tr className="border-y border-border bg-muted/30">
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Paziente</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Data Richiesta</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Provenienza</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Diagnosi</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Priorita</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Stato</th>
                    <th className="text-right py-2.5 px-4 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPatients.map((patient, index) => {
                    const pConfig = priorityConfig[patient.priorita];
                    const isExpiring = isPAIExpiringSoon(patient.id);
                    return (
                      <tr
                        key={patient.id}
                        className={`border-b border-border/40 hover:bg-muted/40 transition-colors duration-100 cursor-pointer ${
                          isExpiring ? 'border-l-[3px] border-l-warning bg-warning/4' : ''
                        }`}
                        style={{ animationDelay: `${index * 30}ms` }}
                        onClick={() => navigate(`/segreteria/richiesta/${patient.id}`)}
                        data-testid={`patient-row-${patient.id}`}
                      >
                        <td className="py-2.5 px-4">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-primary-muted flex items-center justify-center flex-shrink-0">
                              <Users className="w-3.5 h-3.5 text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground leading-tight">{patient.cognome} {patient.nome}</p>
                              <p className="text-[11px] text-muted-foreground">{patient.codiceFiscale}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 px-4 text-sm text-muted-foreground">{patient.dataRichiesta}</td>
                        <td className="py-2.5 px-4">
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm text-muted-foreground truncate max-w-[140px]">{patient.provenienza}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-4">
                          <p className="text-sm text-foreground truncate max-w-[200px]">{patient.diagnosiPrincipale}</p>
                        </td>
                        <td className="py-2.5 px-4">
                          <Badge variant={pConfig.variant} className="text-[11px]">
                            <span className={`w-1.5 h-1.5 rounded-full ${pConfig.dot} mr-1.5`} />
                            {pConfig.label}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-4">
                          <Badge variant="chip" className="text-[11px]">{statoLabels[patient.stato]}</Badge>
                        </td>
                        <td className="py-2.5 px-4 text-right">
                          <Button variant="ghost" size="sm" className="text-primary gap-1 h-7 text-xs px-2">
                            <Eye className="w-3 h-3" />
                            Gestisci
                            <ChevronRight className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {paginatedPatients.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-muted-foreground">
                        Nessuna richiesta trovata
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Cards Mobile */}
            <div className="md:hidden space-y-3">
              {paginatedPatients.map((patient, index) => {
                const pConfig = priorityConfig[patient.priorita];
                return (
                  <motion.div
                    key={patient.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card
                      className="shadow-soft cursor-pointer hover:shadow-card transition-shadow duration-200"
                      onClick={() => navigate(`/segreteria/richiesta/${patient.id}`)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary-muted flex items-center justify-center">
                              <Users className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground">{patient.cognome} {patient.nome}</p>
                              <p className="text-xs text-muted-foreground">{patient.codiceFiscale}</p>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Badge variant={pConfig.variant}>
                            <span className={`w-1.5 h-1.5 rounded-full ${pConfig.dot} mr-1.5`} />
                            {pConfig.label}
                          </Badge>
                          <Badge variant="chip">{patient.provenienza}</Badge>
                          <Badge variant="chip">{patient.dataRichiesta}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {/* Pagination Footer */}
            {totalItems > 0 && (
              <div className="flex items-center justify-between pt-4 mt-2 border-t border-border/50" data-testid="pagination-footer">
                <p className="text-xs text-muted-foreground">
                  Visualizzati <span className="font-medium text-foreground">{showStart}-{showEnd}</span> di <span className="font-medium text-foreground">{totalItems}</span> elementi
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs gap-1"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="w-3 h-3" />
                    Indietro
                  </Button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <Button
                      key={page}
                      variant={currentPage === page ? 'default' : 'outline'}
                      size="sm"
                      className={`h-7 w-7 p-0 text-xs ${currentPage === page ? 'bg-primary text-primary-foreground' : ''}`}
                      onClick={() => setCurrentPage(page)}
                      data-testid={`pagination-page-${page}`}
                    >
                      {page}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs gap-1"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    data-testid="pagination-next"
                  >
                    Avanti
                    <ChevronRight className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
